import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Book, ChevronRight, ChevronDown, Home, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTheme } from '../context/ThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EbookViewerPage = () => {
  const { ebookType } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { isDark } = useTheme();
  
  const [ebook, setEbook] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeChapter, setActiveChapter] = useState(0);
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    fetchEbook();
  }, [ebookType, token]);

  const fetchEbook = async () => {
    try {
      const res = await axios.get(`${API}/ebook/${ebookType}`, {
        params: { token }
      });
      setEbook(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load ebook');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSection = (chapterIdx, sectionIdx) => {
    const key = `${chapterIdx}-${sectionIdx}`;
    setExpandedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <Loader2 className="animate-spin text-blue-500" size={48} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className={`max-w-md p-8 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-xl text-center`}>
          <p className="text-red-500 mb-4">{error}</p>
          <Link to="/">
            <Button>Return Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`} data-testid="ebook-viewer">
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm" className="flex items-center gap-1">
                  <ArrowLeft size={16} />
                  Back
                </Button>
              </Link>
              <div>
                <h1 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {ebook?.title}
                </h1>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {ebook?.subtitle}
                </p>
              </div>
            </div>
            <Book className={`${isDark ? 'text-amber-400' : 'text-amber-500'}`} size={28} />
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Table of Contents Sidebar */}
          <aside className={`lg:col-span-1 ${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl p-4 h-fit lg:sticky lg:top-24 shadow-sm`}>
            <h2 className={`font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Contents</h2>
            <nav className="space-y-1">
              {ebook?.chapters?.map((chapter, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveChapter(idx)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    activeChapter === idx
                      ? isDark ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700 font-medium'
                      : isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {chapter.title}
                </button>
              ))}
            </nav>
          </aside>

          {/* Main Content */}
          <main className={`lg:col-span-3 ${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl p-6 lg:p-8 shadow-sm`}>
            {ebook?.chapters?.[activeChapter] && (
              <div>
                {/* Chapter Title */}
                <h2 className={`text-2xl lg:text-3xl font-bold mb-6 pb-4 border-b ${isDark ? 'text-white border-gray-700' : 'text-gray-900 border-gray-200'}`}>
                  {ebook.chapters[activeChapter].title}
                </h2>

                {/* Sections */}
                <div className="space-y-4">
                  {ebook.chapters[activeChapter].sections?.map((section, sectionIdx) => {
                    const isExpanded = expandedSections[`${activeChapter}-${sectionIdx}`] !== false;
                    
                    return (
                      <div
                        key={sectionIdx}
                        className={`border rounded-xl overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
                      >
                        {/* Section Header */}
                        <button
                          onClick={() => toggleSection(activeChapter, sectionIdx)}
                          className={`w-full flex items-center justify-between p-4 text-left ${isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`}
                        >
                          <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {section.heading}
                          </h3>
                          {isExpanded ? (
                            <ChevronDown size={20} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
                          ) : (
                            <ChevronRight size={20} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
                          )}
                        </button>

                        {/* Section Content */}
                        {isExpanded && (
                          <div className={`p-4 pt-0 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            <div 
                              className="prose prose-sm max-w-none"
                              style={{
                                lineHeight: 1.8,
                                whiteSpace: 'pre-wrap'
                              }}
                            >
                              {section.content.split('\n').map((paragraph, pIdx) => {
                                // Handle bold text
                                const formattedParagraph = paragraph.replace(
                                  /\*\*(.*?)\*\*/g,
                                  '<strong>$1</strong>'
                                );
                                
                                // Check if it's a header line
                                if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                                  return (
                                    <h4 
                                      key={pIdx} 
                                      className={`font-bold mt-4 mb-2 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}
                                      dangerouslySetInnerHTML={{ __html: formattedParagraph }}
                                    />
                                  );
                                }
                                
                                // Check if it's a list item
                                if (paragraph.startsWith('- ') || paragraph.startsWith('• ')) {
                                  return (
                                    <li 
                                      key={pIdx} 
                                      className="ml-4"
                                      dangerouslySetInnerHTML={{ __html: formattedParagraph.substring(2) }}
                                    />
                                  );
                                }
                                
                                // Check if it's a table row
                                if (paragraph.includes('|') && paragraph.split('|').length > 2) {
                                  const cells = paragraph.split('|').filter(c => c.trim());
                                  if (cells.length > 0 && !paragraph.includes('---')) {
                                    return (
                                      <div key={pIdx} className={`grid grid-cols-${cells.length} gap-2 text-sm py-1 ${paragraph.includes('**') ? 'font-bold' : ''}`}>
                                        {cells.map((cell, cIdx) => (
                                          <span key={cIdx} dangerouslySetInnerHTML={{ __html: cell.replace(/\*\*/g, '') }} />
                                        ))}
                                      </div>
                                    );
                                  }
                                  return null;
                                }
                                
                                // Regular paragraph
                                if (paragraph.trim()) {
                                  return (
                                    <p 
                                      key={pIdx} 
                                      className="mb-2"
                                      dangerouslySetInnerHTML={{ __html: formattedParagraph }}
                                    />
                                  );
                                }
                                
                                return <br key={pIdx} />;
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Navigation */}
                <div className="flex justify-between mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <Button
                    variant="outline"
                    disabled={activeChapter === 0}
                    onClick={() => setActiveChapter(prev => prev - 1)}
                  >
                    Previous Chapter
                  </Button>
                  <Button
                    variant="outline"
                    disabled={activeChapter === ebook.chapters.length - 1}
                    onClick={() => setActiveChapter(prev => prev + 1)}
                  >
                    Next Chapter
                  </Button>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default EbookViewerPage;
